import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../../../../core/auth/auth.service';
import { UserProfileService } from '../../../../../../services/user-profile.service';
import { WaypointService } from '../../../../../../services/waypoint.service';
import { VesselService } from '../../../../../../services/vessel.service';
import { SocialService } from '../../../../../../services/social.service';
import { MapOrientationMode, SubMenuService } from '../../../../../../services/submenu.service';

import { MatIconModule } from '@angular/material/icon';
import { BackButtonComponent } from '../../../../../../shared/components/buttons/back-button/back-button.component';
import { ConfirmButtonComponent } from '../../../../../../shared/components/buttons/confirm-button/confirm-button.component';
import { CameraIconButtonComponent } from '../../../../../../shared/components/buttons/camera-button/camera-button.component';
import { EditButtonComponent } from '../../../../../../shared/components/buttons/edit-button/edit-button.component';
import { ToastService } from '../../../../../../services/toast.service';
import { ExitButtonComponent } from '../../../../../../shared/components/buttons/exit-button/exit-button.component';
import { TourAnchorDirective } from '../../../../../../features/learning-center/engine/tour-anchor.directive';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, ExitButtonComponent, FormsModule, MatIconModule, BackButtonComponent, ConfirmButtonComponent, CameraIconButtonComponent, EditButtonComponent, TourAnchorDirective],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit, OnDestroy {
  private subs = new Subscription();
  @Output() profileUpdated = new EventEmitter<any>();
  showProfileOverlay = false;
  showDefaultSharingOverlay = false;
  showCrewOverlay = false;
  showSecurityOverlay = false;
  showBlockedUsersOverlay = false;
  showUnitsOverlay = false;
  showPositionFormatOverlay = false;
  showMapSettingsOverlay = false;
  showAutoTripsOverlay = false;
  showWaterDropOverlay = false;
  showAdminSettingsOverlay = false;
  showOfflineOverlay = false;
  showSupportOverlay = false;
  showDeleteConfirm = false;
  showLogoutConfirm = false;

  isLoading = false;
  isSaving = false;
  sharingSearchQuery = '';
  fleets: any[] = [];
  dockmates: any[] = [];
  sharedFriendIds: any[] = [];
  private originalProfileData: any = null;

  // ── Default Crew ──
  selectedCrewIds = new Set<string>();
  crewSearchQuery = '';
  isSavingCrew = false;

  // ── Blocked Users ──
  blockedUsers: any[] = [];
  isLoadingBlockedUsers = false;
  isUnblockingId: string | null = null;

  // ── Position Format (Local preference) ──
  selectedCoordinateFormat: 'ddm' | 'dms' | 'loran' = 'ddm';

  // ── Map Settings & Skins Catalog (mirrors MapSkinCatalog in map_skin_prefs.dart) ──
  readonly classicSkins = [
    { id: 'nautical', label: 'Nautical', description: 'Soft tones. The Esperwave standard.', thumbnail: 'assets/images/map_skins/nautical.png' },
    { id: 'vivid', label: 'Vivid', description: 'Bright water, high-contrast depth.', thumbnail: 'assets/images/map_skins/vivid.png' },
    { id: 'night', label: 'Night', description: 'Dark chart for low-light running.', thumbnail: 'assets/images/map_skins/night.png' },
    { id: 'satellite', label: 'Satellite', description: 'Aerial imagery under the contours.', thumbnail: 'assets/images/map_skins/satellite.jpg' },
  ];

  readonly depthSkins = [
    { id: 'depth_relief', label: 'Relief', description: 'Shaded sea floor, colored by depth.', thumbnail: 'assets/images/map_skins/depth_relief.png' },
    { id: 'depth_sonar', label: 'Sonar', description: 'Depth contours on a dark chart, labelled in feet.', thumbnail: 'assets/images/map_skins/depth_sonar.png' },
    { id: 'depth_combo', label: 'Combo', description: 'Shaded sea floor with contour lines.', thumbnail: 'assets/images/map_skins/depth_combo.png' },
  ];
  selectedSkinId: string = 'nautical';
  pointClickEnabled: boolean = true;
  enableTracklines: boolean = true;
  fadeOldTracklines: boolean = false;

  // ── Automated Trip Logging ──
  autoStartSuppression: boolean = false;
  autoTripPhone: boolean = true;

  // ── Water Drop (admin-only) ──
  isWaterDropUnlocked: boolean = false;
  waterDropEnabled: boolean = true;
  waterDropAlerts: boolean = true;

  // ── Admin Settings ──
  isAdmin: boolean = false;
  tripCachingEnabled: boolean = true;
  adminDataBridgeGlobal: boolean = true;
  adminPhoneGlobal: boolean = true;
  adminWaterDropGlobal: boolean = true;

  // ── Offline Mode ──
  selectedOfflineRange: string = '24h';
  isDownloadingOffline: boolean = false;
  offlineDownloadProgress: number = 0;

  profileData: any = {
    first_name: '',
    last_name: '',
    email_address: { email_address: '' },
    phone_address: { phone_number: '' },
    image_url: null,
    allow_contact: true,
  };

  unitsData: any = {
    settings: {
      gps_type: 'GPS',
      distance_unit: 'nautical',
      speed_unit: 'knots',
      temperature_unit: 'celsius',
      depth_unit: 'feet',
      wind_unit: 'knots',
      visibility_unit: 'feet',
      map_orientation: {
        mode: 'normal'
      },
      privacy: {
        default_visibility: 0,
        default_friend_ids: [],
        default_fleet_ids: [],
        default_followers: [],
        default_crew_friend_ids: [],
        default_share_message: '',
        offline_data_range: '24h',
      }
    },
  };

  securityData = {
    email: '',
  };

  // ── Contact Support (mirrors the app's support_sheet.dart) ──
  private static readonly SUPPORT_EMAIL = 'support@esperwave.com';
  supportSubject = '';
  supportMessage = '';
  supportImages: { file: File; url: string }[] = [];
  isSendingSupport = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private userProfileService: UserProfileService,
    private waypointService: WaypointService,
    private socialService: SocialService,
    private submenuService: SubMenuService,
    public toast: ToastService,
  ) { }

  ngOnInit() {
    this.fetchProfileData();
    this.checkAdminStatus();
    this.subs.add(
      this.submenuService.supportOverlay$.subscribe(() => {
        this.toggleSupportOverlay(true);
      })
    );
  }

  private checkAdminStatus() {
    this.isAdmin = this.authService.hasAdminRole();
    this.isWaterDropUnlocked = this.isAdmin;
    this.authService.getUserRoles().subscribe({
      next: (res: any) => {
        const roles: string[] = Array.isArray(res) ? res : (res?.roles || []);
        const hasAdmin = roles.some((r: string) => {
          const lower = String(r).toLowerCase();
          return (
            lower === 'spectra.admin' ||
            lower === 'admin' ||
            lower === 'spectra_admin' ||
            lower === 'superadmin' ||
            lower === 'realm-admin'
          );
        });
        if (hasAdmin) {
          this.isAdmin = true;
          this.isWaterDropUnlocked = true;
        } else if (!this.authService.hasAdminRole()) {
          this.isAdmin = false;
          this.isWaterDropUnlocked = false;
        }
      },
      error: () => {}
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.clearSupportImages();
  }

  toggleProfileOverlay(show: boolean) {
    this.showProfileOverlay = show;
    if (show) {
      this.fetchProfileData();
    }
  }

  toggleDefaultSharingOverlay(show: boolean) {
    this.showDefaultSharingOverlay = show;
    if (show) {
      this.fetchProfileData();
      if (this.fleets.length === 0 && this.dockmates.length === 0) {
        this.fetchFleetsAndFriends();
      }
    }
  }

  toggleCrewOverlay(show: boolean) {
    this.showCrewOverlay = show;
    if (show) {
      this.fetchProfileData();
      const rawCrew = this.unitsData.settings.privacy?.default_crew_friend_ids;
      this.selectedCrewIds = new Set(Array.isArray(rawCrew) ? rawCrew : []);
      if (this.dockmates.length === 0) {
        this.fetchFleetsAndFriends();
      }
    }
  }

  isCrewSelected(userId: string): boolean {
    return this.selectedCrewIds.has(userId);
  }

  toggleCrewMember(userId: string) {
    if (this.selectedCrewIds.has(userId)) {
      this.selectedCrewIds.delete(userId);
    } else {
      this.selectedCrewIds.add(userId);
    }
  }

  get filteredCrewDockmates() {
    if (!this.crewSearchQuery) return this.dockmates;
    const query = this.crewSearchQuery.toLowerCase();
    return this.dockmates.filter(m => (m.user?.name || m.name)?.toLowerCase().includes(query));
  }

  saveDefaultCrew() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.isSavingCrew = true;
    const privacy = {
      ...(this.unitsData.settings.privacy || {}),
      default_crew_friend_ids: Array.from(this.selectedCrewIds),
    };
    this.unitsData.settings.privacy = privacy;

    this.userProfileService.UpdateProfile(userId, { settings: this.unitsData.settings }).subscribe({
      next: () => {
        this.isSavingCrew = false;
        this.toggleCrewOverlay(false);
        this.showToast('Default crew settings updated', 'success');
      },
      error: (err) => {
        console.error('Failed to update default crew:', err);
        this.isSavingCrew = false;
        this.showToast('Failed to update default crew settings', 'error');
      }
    });
  }

  toggleSecurityOverlay(show: boolean) {
    this.showSecurityOverlay = show;
    if (show) {
      this.securityData.email = this.authService.getLastEmail() || '';
      this.fetchProfileData();
    }
  }

  toggleBlockedUsersOverlay(show: boolean) {
    this.showBlockedUsersOverlay = show;
    if (show) {
      this.loadBlockedUsers();
    }
  }

  loadBlockedUsers() {
    this.isLoadingBlockedUsers = true;
    this.socialService.getBlockedUsers(50, 0).subscribe({
      next: (res: any) => {
        this.blockedUsers = Array.isArray(res) ? res : (res?.results || res?.data || []);
        this.isLoadingBlockedUsers = false;
      },
      error: (err) => {
        console.error('Failed to load blocked users:', err);
        this.blockedUsers = [];
        this.isLoadingBlockedUsers = false;
      }
    });
  }

  unblockUser(user: any) {
    const targetId = user.user_id || user.id || user._id;
    if (!targetId || this.isUnblockingId) return;

    this.isUnblockingId = targetId;
    this.socialService.unblockUser(targetId).subscribe({
      next: () => {
        this.blockedUsers = this.blockedUsers.filter(u => (u.user_id || u.id || u._id) !== targetId);
        this.isUnblockingId = null;
        this.showToast(`Unblocked ${user.name || 'captain'}`, 'success');
      },
      error: (err) => {
        console.error('Failed to unblock user:', err);
        this.isUnblockingId = null;
        this.showToast('Failed to unblock user. Please try again.', 'error');
      }
    });
  }

  toggleUnitsOverlay(show: boolean) {
    this.showUnitsOverlay = show;
    if (show) {
      this.fetchProfileData();
    }
  }

  togglePositionFormatOverlay(show: boolean) {
    this.showPositionFormatOverlay = show;
    if (show) {
      const stored = localStorage.getItem('coordinate_format') as any;
      if (stored === 'dms' || stored === 'loran' || stored === 'ddm') {
        this.selectedCoordinateFormat = stored;
      } else {
        this.selectedCoordinateFormat = 'ddm';
      }
    }
  }

  selectCoordinateFormat(format: 'ddm' | 'dms' | 'loran') {
    this.selectedCoordinateFormat = format;
    localStorage.setItem('coordinate_format', format);
  }

  savePositionFormat() {
    if (this.selectedCoordinateFormat) {
      localStorage.setItem('coordinate_format', this.selectedCoordinateFormat);
    }
    this.togglePositionFormatOverlay(false);
    this.showToast('Position format saved', 'success');
  }

  toggleMapSettingsOverlay(show: boolean) {
    this.showMapSettingsOverlay = show;
    if (show) {
      this.fetchProfileData();
      this.selectedSkinId = localStorage.getItem('map_skin_id') || 'nautical';
      this.pointClickEnabled = localStorage.getItem('point_click_enabled') !== 'false';
      this.enableTracklines = localStorage.getItem('enable_tracklines') !== 'false';
      this.fadeOldTracklines = localStorage.getItem('fade_old_tracklines') === 'true';
    }
  }

  selectMapSkin(skinId: string) {
    this.selectedSkinId = skinId;
  }

  getSelectedSkin(): any {
    return (
      this.classicSkins.find(s => s.id === this.selectedSkinId) ||
      this.depthSkins.find(s => s.id === this.selectedSkinId) ||
      this.classicSkins[0]
    );
  }

  selectMapOrientation(mode: MapOrientationMode) {
    this.unitsData.settings.map_orientation = {
      ...(this.unitsData.settings.map_orientation || {}),
      mode,
    };
    this.submenuService.setMapOrientationMode(mode);
    this.submenuService.notifyUserSettingsUpdated(this.unitsData.settings);
  }

  saveMapSettings() {
    localStorage.setItem('map_skin_id', this.selectedSkinId);
    localStorage.setItem('point_click_enabled', String(this.pointClickEnabled));
    localStorage.setItem('enable_tracklines', String(this.enableTracklines));
    localStorage.setItem('fade_old_tracklines', String(this.fadeOldTracklines));

    this.saveMapOrientation();
    this.toggleMapSettingsOverlay(false);
    this.showToast('Map settings updated', 'success');
  }

  toggleAutoTripsOverlay(show: boolean) {
    this.showAutoTripsOverlay = show;
    if (show) {
      this.fetchProfileData();
      this.autoStartSuppression = localStorage.getItem('auto_start_suppressed') === 'true';
      this.autoTripPhone = this.unitsData.settings?.auto_trip_phone ?? this.unitsData.settings?.privacy?.auto_trip_phone ?? true;
    }
  }

  // ── Read-only overlays: mutations disabled ──
  toggleAutoStartSuppression() {
    // Read-only setting: user cannot change
  }

  toggleAutoTripPhone() {
    // Read-only setting: user cannot change
  }

  saveAutoTripSettings() {
    // Read-only setting: user cannot change
    this.toggleAutoTripsOverlay(false);
  }

  toggleWaterDropOverlay(show: boolean) {
    if (show && !this.isAdmin) {
      return;
    }
    this.showWaterDropOverlay = show;
    if (show) {
      this.waterDropEnabled = localStorage.getItem('water_drop_enabled') !== 'false';
      this.waterDropAlerts = localStorage.getItem('water_drop_alerts') !== 'false';
    }
  }

  saveWaterDropSettings() {
    // Read-only setting: user cannot change
    this.toggleWaterDropOverlay(false);
  }

  toggleAdminSettingsOverlay(show: boolean) {
    this.showAdminSettingsOverlay = show;
    if (show) {
      this.tripCachingEnabled = localStorage.getItem('admin_trip_caching') !== 'false';
      this.adminDataBridgeGlobal = localStorage.getItem('admin_databridge_global') !== 'false';
      this.adminPhoneGlobal = localStorage.getItem('admin_phone_global') !== 'false';
      this.adminWaterDropGlobal = localStorage.getItem('admin_water_drop_global') !== 'false';
    }
  }

  saveAdminSettings() {
    // Read-only setting: user cannot change
    this.toggleAdminSettingsOverlay(false);
  }

  toggleOfflineOverlay(show: boolean) {
    this.showOfflineOverlay = show;
    if (show) {
      const stored = this.unitsData.settings?.privacy?.offline_data_range || localStorage.getItem('offline_data_range');
      if (stored) this.selectedOfflineRange = stored;
    }
  }

  selectOfflineRange(range: string) {
    this.selectedOfflineRange = range;
    localStorage.setItem('offline_data_range', range);
  }

  startOfflineDownload() {
    this.isDownloadingOffline = true;
    this.offlineDownloadProgress = 15;
    const interval = setInterval(() => {
      this.offlineDownloadProgress += 30;
      if (this.offlineDownloadProgress >= 100) {
        clearInterval(interval);
        this.isDownloadingOffline = false;
        this.offlineDownloadProgress = 0;
        const userId = this.authService.getUserIdFromToken();
        if (userId) {
          const privacy = {
            ...(this.unitsData.settings.privacy || {}),
            offline_data_range: this.selectedOfflineRange,
          };
          this.unitsData.settings.privacy = privacy;
          this.userProfileService.UpdateProfile(userId, { settings: this.unitsData.settings }).subscribe();
        }
        this.toggleOfflineOverlay(false);
        this.showToast('Offline data downloaded', 'success');
      }
    }, 350);
  }

  toggleSupportOverlay(show: boolean) {
    this.showSupportOverlay = show;
    // The app builds the sheet fresh each time it opens, so start from a blank
    // form on every open rather than restoring whatever was typed before.
    if (show) {
      this.resetSupportForm();
      this.fetchProfileData();
    }
  }

  onSupportImagesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files || []);
    // Merge, skipping duplicates the way the app dedupes by file path.
    const existing = new Set(
      this.supportImages.map((i) => `${i.file.name}:${i.file.size}:${i.file.lastModified}`),
    );
    for (const file of picked) {
      const key = `${file.name}:${file.size}:${file.lastModified}`;
      if (existing.has(key)) continue;
      existing.add(key);
      this.supportImages.push({ file, url: URL.createObjectURL(file) });
    }
    // Reset so re-picking the same file still fires a change event.
    input.value = '';
  }

  removeSupportImage(index: number) {
    const [removed] = this.supportImages.splice(index, 1);
    if (removed) URL.revokeObjectURL(removed.url);
  }

  /**
   * Hands the report off to the user's mail client, like the app does.
   *
   * Web can't attach files to a `mailto:` link, so when screenshots are picked
   * we try the Web Share sheet first (that *can* carry files into a mail app on
   * browsers that support it) and fall back to `mailto:` with a note telling
   * the user to attach the images themselves.
   */
  async submitSupport() {
    const subject = this.supportSubject.trim();
    const message = this.supportMessage.trim();
    if (!message) {
      this.showToast('Please describe your issue before sending.', 'warning');
      return;
    }

    this.isSendingSupport = true;
    try {
      const mailSubject = subject ? `[Report] ${subject}` : '[EsperWave] Report';
      const body = `${message}${this.buildAccountInfo()}`;
      const files = this.supportImages.map((i) => i.file);

      const nav = navigator as any;
      if (files.length && nav.canShare?.({ files }) && nav.share) {
        await nav.share({ title: mailSubject, text: body, files });
      } else {
        const mailto =
          `mailto:${SettingsComponent.SUPPORT_EMAIL}` +
          `?subject=${encodeURIComponent(mailSubject)}` +
          `&body=${encodeURIComponent(body)}`;
        window.location.href = mailto;
        if (files.length) {
          this.showToast(
            'Your mail app can\'t receive attachments from the browser — please attach the screenshots there.',
            'warning',
          );
        }
      }

      this.toggleSupportOverlay(false);
    } catch (err: any) {
      // A cancelled share sheet isn't an error worth surfacing.
      if (err?.name !== 'AbortError') {
        console.error('Error sending support report:', err);
        this.showToast('Could not open your mail app. Please try again.', 'error');
      }
    } finally {
      this.isSendingSupport = false;
    }
  }

  /** The `--- Account Info ---` footer the app appends to every report. */
  private buildAccountInfo(): string {
    const name = [this.profileData?.first_name, this.profileData?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    const email = this.profileData?.email_address?.email_address || '';
    const userId = this.profileData?.id || this.profileData?._id || '';

    let info = '\n\n--- Account Info ---\n';
    if (name) info += `Name: ${name}\n`;
    if (email) info += `Email: ${email}\n`;
    if (userId) info += `User ID: ${userId}\n`;
    return info;
  }

  private resetSupportForm() {
    this.supportSubject = '';
    this.supportMessage = '';
    this.clearSupportImages();
  }

  private clearSupportImages() {
    this.supportImages.forEach((i) => URL.revokeObjectURL(i.url));
    this.supportImages = [];
  }


  fetchProfileData() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.isLoading = true;
    this.userProfileService.getProfileById(userId).subscribe({
      next: (res: any) => {
        this.profileData = { ...this.profileData, ...res };
        this.profileData.email_address = {
          email_address: this.profileData?.email_address?.email_address ?? '',
        };
        this.profileData.phone_address = {
          phone_number: this.profileData?.phone_address?.phone_number ?? '',
        };
        this.originalProfileData = this.getProfileSnapshot(this.profileData);
        if (res.settings) {
          this.unitsData.settings = {
            ...this.unitsData.settings,
            ...res.settings,
            map_orientation: {
              ...(this.unitsData.settings.map_orientation || {}),
              ...(res.settings.map_orientation || {}),
            },
            privacy: {
              ...(this.unitsData.settings.privacy || {}),
              ...(res.settings.privacy || {})
            }
          };
          if (res.settings.privacy) {
            const privacy = res.settings.privacy;
            this.sharedFriendIds = privacy.default_friend_ids || [];
            this.syncSelections(privacy);
          }
        }
        // Keep email in sync so Forgot Password view always shows current email
        if (this.profileData.email_address?.email_address) {
          this.securityData.email = this.profileData.email_address.email_address;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching profile data:', err);
        this.isLoading = false;
      },
    });
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;

    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.isLoading = true;
    this.authService.updateProfileImage(userId, input.files[0]).subscribe({
      next: (res: any) => {
        this.profileData = { ...this.profileData, ...res };
        this.profileUpdated.emit(this.profileData);
        this.isLoading = false;
        this.showToast('Profile photo updated', 'success');
      },
      error: (err) => {
        console.error('Error updating profile image:', err);
        this.isLoading = false;
        this.showToast('Failed to update profile photo. Please try again.', 'error');
      },
    });
  }

  saveProfile() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    const payload = this.buildProfilePatchPayload();
    if (Object.keys(payload).length === 0) {
      this.toggleProfileOverlay(false);
      return;
    }

    this.isLoading = true;
    this.userProfileService.UpdateProfile(userId, payload).subscribe({
      next: (res: any) => {
        this.profileData = { ...this.profileData, ...res };
        this.profileData.email_address = {
          email_address: this.profileData?.email_address?.email_address ?? '',
        };
        this.profileData.phone_address = {
          phone_number: this.profileData?.phone_address?.phone_number ?? '',
        };
        this.originalProfileData = this.getProfileSnapshot(this.profileData);
        this.profileUpdated.emit(this.profileData);
        this.toggleProfileOverlay(false);
        this.isLoading = false;
        this.showToast('Profile updated', 'success');
      },
      error: (err) => {
        console.error('Error updating profile:', err);
        this.isLoading = false;
        this.showToast('Failed to update profile. Please try again.', 'error');
      },
    });
  }

  private getProfileSnapshot(profile: any) {
    return {
      first_name: profile?.first_name ?? '',
      last_name: profile?.last_name ?? '',
      allow_contact: profile?.allow_contact ?? true,
      email_address: {
        email_address: profile?.email_address?.email_address ?? '',
      },
      phone_address: {
        phone_number: profile?.phone_address?.phone_number ?? '',
      },
    };
  }

  private buildProfilePatchPayload() {
    const current = this.getProfileSnapshot(this.profileData);
    const original = this.originalProfileData || this.getProfileSnapshot(this.profileData);
    const payload: any = {};

    if (current.first_name !== original.first_name) {
      payload.first_name = current.first_name;
    }
    if (current.last_name !== original.last_name) {
      payload.last_name = current.last_name;
    }
    if (current.allow_contact !== original.allow_contact) {
      payload.allow_contact = current.allow_contact;
    }
    if (current.email_address.email_address !== original.email_address.email_address) {
      payload.email_address = {
        email_address: current.email_address.email_address,
      };
    }
    if (current.phone_address.phone_number !== original.phone_address.phone_number) {
      payload.phone_address = {
        phone_number: current.phone_address.phone_number,
      };
    }

    return payload;
  }

  saveUnits() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.isLoading = true;
    this.userProfileService.UpdateProfile(userId, this.unitsData).subscribe({
      next: (res: any) => {
        this.submenuService.notifyUserSettingsUpdated({
          ...(res?.settings || {}),
          ...this.unitsData.settings,
        });
        this.toggleUnitsOverlay(false);
        this.isLoading = false;
        this.showToast('Units updated', 'success');
      },
      error: (err) => {
        console.error('Error updating units:', err);
        this.isLoading = false;
        this.showToast('Failed to update units. Please try again.', 'error');
      },
    });
  }

  saveMapOrientation() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.isLoading = true;
    this.userProfileService.UpdateProfile(userId, this.unitsData).subscribe({
      next: (res: any) => {
        this.submenuService.notifyUserSettingsUpdated({
          ...(res?.settings || {}),
          ...this.unitsData.settings,
        });
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error updating map orientation:', err);
        this.isLoading = false;
      },
    });
  }

  toggleMapOrientationOverlay(show: boolean) {
    this.toggleMapSettingsOverlay(show);
  }

  saveDefaultSharing() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    const fleetIds = this.fleets.filter(f => f.selected).map(f => f.id || f._id);
    const friendIds = this.dockmates.filter(m => m.selected).map(m => m.user?.user_id || m.user?._id || m.user?.id || m.id);

    this.unitsData.settings.privacy.default_fleet_ids = fleetIds;
    this.unitsData.settings.privacy.default_friend_ids = friendIds;

    const payload = {
      settings: {
        privacy: this.unitsData.settings.privacy
      }
    };

    this.isLoading = true;
    this.userProfileService.UpdateProfile(userId, payload).subscribe({
      next: (res) => {
        this.toggleDefaultSharingOverlay(false);
        this.isLoading = false;
        this.showToast('Default sharing updated', 'success');
      },
      error: (err) => {
        console.error('Error updating default sharing:', err);
        this.isLoading = false;
        this.showToast('Failed to update default sharing. Please try again.', 'error');
      },
    });
  }

  fetchFleetsAndFriends() {
    this.waypointService.getFleets(1, 100).subscribe({
      next: (res) => {
        const privacy = this.unitsData.settings.privacy;
        this.fleets = (res.results || []).map((f: any) => ({
          ...f,
          selected: privacy?.default_fleet_ids?.includes(f.id || f._id)
        }));
      }
    });
    this.socialService.myDockmates(1, 100).subscribe({
      next: (res: any) => {
        const tokenUserId = this.authService.getUserIdFromToken();
        const objUserId = this.authService.getUserObjectId();
        const results = (res.results || []).filter((m: any) => {
          const uId = m.user?.user_id || m.user?._id || m._id;
          return uId !== tokenUserId && uId !== objUserId;
        });

        this.dockmates = results.map((m: any) => {
          const uId = m.user?.user_id || m.user?._id;
          return {
            ...m,
            name: m.user?.name || m.display_name || m.name,
            image_url: m.user?.image_url || m.image_url,
            selected: this.sharedFriendIds.includes(uId) || this.sharedFriendIds.includes(m.user?._id),
          };
        });
      },
      error: (err) => {
        console.error('Error fetching dockmates:', err);
      }
    });
  }

  syncSelections(privacy: any) {
    if (this.fleets.length > 0) {
      this.fleets.forEach(f => {
        const id = f.id || f._id;
        f.selected = privacy.default_fleet_ids?.includes(id);
      });
    }
    if (this.dockmates.length > 0) {
      this.dockmates.forEach(m => {
        const userId = m.user?.id || m.user?._id || m.id;
        m.selected = privacy.default_friend_ids?.includes(userId);
      });
    }
  }

  get filteredFleets() {
    if (!this.sharingSearchQuery) return this.fleets;
    return this.fleets.filter(f => f.name?.toLowerCase().includes(this.sharingSearchQuery.toLowerCase()));
  }

  get filteredDockmates() {
    if (!this.sharingSearchQuery) return this.dockmates;
    return this.dockmates.filter(m => (m.user?.name || m.name)?.toLowerCase().includes(this.sharingSearchQuery.toLowerCase()));
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }

  showToast(message: string, type: 'success' | 'error' | 'warning') {
    if (type === 'success') this.toast.success(message);
    else if (type === 'warning') this.toast.warning(message);
    else this.toast.error(message);
  }

  requestPasswordReset() {
    if (!this.securityData.email) {
      this.showToast('No email address found', 'error');
      return;
    }

    this.isLoading = true;
    this.authService.forgotPassword(this.securityData.email).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.toggleSecurityOverlay(false);
        this.showToast('Password reset instructions sent to your email', 'success');
      },
      error: (err) => {
        console.error('Error requesting password reset:', err);
        this.isLoading = false;
        this.showToast('Failed to send reset email. Please try again.', 'error');
      },
    });
  }

  confirmDeleteAccount() {
    this.showDeleteConfirm = false;
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.isLoading = true;
    this.authService.deleteAccount(userId).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.logout();
      },
      error: (err) => {
        console.error('Error deleting account:', err);
        this.isLoading = false;
        this.showToast('Failed to delete account. Please try again.', 'error');
      },
    });
  }

  logout() {
    this.showLogoutConfirm = false;
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }
}
